import React from 'react'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Icon from '@amzn/meridian/icon'
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { ICategoryGroup } from '../types'
import { CATEGORY_LABEL, CATEGORY_OTHER, TASK_COUNT } from '../constants'
import { isKnownCategory, countUnread } from '../utils'
import { CategoryIcon } from './CategoryIcon'

interface ICategoryAccordionProps {
    groups: ICategoryGroup[]
    onSelect: (category: string) => void
}

/** Label for a category row: translated for known categories, "Other" for none, the raw wire value otherwise. */
export const useCategoryLabel = (): ((category: string) => string) => {
    const [bundle] = useBundle(BUNDLE_NAME)
    return (category: string) => {
        if (isKnownCategory(category)) return t(bundle, CATEGORY_LABEL[category])
        return category || t(bundle, CATEGORY_OTHER)
    }
}

/**
 * The rows below "Needs immediate attention": one per category holding MEDIUM/LOW items, with a
 * task count and a chevron. Tapping a row opens that category's list.
 *
 * The count is the category's unread items, the same rule as the tab badges. A category whose
 * items are all acknowledged keeps its row (the history is still one tap away) but shows no
 * count rather than "0 tasks".
 */
export const CategoryAccordion: React.FC<ICategoryAccordionProps> = ({ groups, onSelect }) => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const labelFor = useCategoryLabel()

    if (groups.length === 0) return null

    return (
        <div className="action-center-accordion" data-testid="category-accordion">
            {groups.map((group) => {
                const unread = countUnread(group.notifications)
                return (
                    <button
                        key={group.category}
                        type="button"
                        className="action-center-accordion__row"
                        data-testid="category-row"
                        data-category={group.category}
                        onClick={() => onSelect(group.category)}
                    >
                        <Row alignmentVertical="center" spacing="300" widths={['fit', 'fill', 'fit', 'fit']}>
                            <CategoryIcon category={group.category} label={labelFor(group.category)} />
                            <Text type="b300">{labelFor(group.category)}</Text>
                            {unread > 0 && (
                                <Text type="b300" color="secondary" data-testid="category-count" data-count={unread}>
                                    {t(bundle, TASK_COUNT, { count: unread })}
                                </Text>
                            )}
                            <Icon tokens={chevronRightSmallTokens} />
                        </Row>
                    </button>
                )
            })}
        </div>
    )
}
